import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { toast } from 'react-hot-toast';
import { 
  Shield, 
  ShieldOff, 
  UserCheck, 
  UserX, 
  Search, 
  Filter,
  Eye,
  BarChart3,
  RefreshCw,
  Users,
  Trash2,
  Key
} from 'lucide-react';
import { adminService } from '../services/adminService';
import Navigation from '../components/Navigation';
import { Trainer } from '../types';
import { useAuth } from '../hooks/useAuth';

interface TrainerStats {
  trainer_id: number;
  trainer_name: string;
  email: string;
  is_active: boolean;
  is_admin: boolean;
  created_at: string;
  stats: {
    active_clients_count: number;
    exercises_count: number;
    workouts_count: number;
    action_logs_count: number;
  };
  recent_actions: Array<{
    action_type: string;
    resource_type: string;
    description: string;
    created_at: string;
  }>;
}

const AdminPage: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [activeFilter, setActiveFilter] = useState<boolean | null>(null);
  const [adminFilter, setAdminFilter] = useState<boolean | null>(null);
  const [selectedTrainer, setSelectedTrainer] = useState<Trainer | null>(null);
  const [showStats, setShowStats] = useState(false);
  const [statsData, setStatsData] = useState<TrainerStats | null>(null);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordTrainer, setPasswordTrainer] = useState<Trainer | null>(null);
  const [newPassword, setNewPassword] = useState('');

  const queryClient = useQueryClient();
  const { trainer: currentTrainer } = useAuth();

  // Получение списка тренеров
  const {
    data: trainers = [],
    isLoading,
    error,
    refetch
  } = useQuery(
    ['admin-trainers', searchTerm, activeFilter, adminFilter],
    () => adminService.getTrainers({
      search: searchTerm || undefined,
      is_active: activeFilter || undefined,
      is_admin: adminFilter || undefined
    }),
    {
      retry: false,
      refetchOnWindowFocus: false,
    }
  );



  // Мутация для переключения статуса администратора
  const toggleAdminMutation = useMutation(
    (trainerId: number) => adminService.toggleAdminStatus(trainerId),
    {
      onSuccess: (data) => {
        toast.success(data.message);
        queryClient.invalidateQueries(['admin-trainers']);
        if (selectedTrainer?.id === data.trainer_id) {
          setSelectedTrainer(prev => prev ? { ...prev, is_admin: data.is_admin } : null);
        }
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.detail || 'Ошибка при изменении прав администратора');
      }
    }
  );

  // Мутация для переключения статуса активности
  const toggleActiveMutation = useMutation(
    (trainerId: number) => adminService.toggleActiveStatus(trainerId),
    {
      onSuccess: (data) => {
        toast.success(data.message);
        queryClient.invalidateQueries(['admin-trainers']);
        if (selectedTrainer?.id === data.trainer_id) {
          setSelectedTrainer(prev => prev ? { ...prev, is_active: data.is_active } : null);
        }
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.detail || 'Ошибка при изменении статуса активности');
      }
    }
  );

  // Мутация для удаления тренера
  const deleteTrainerMutation = useMutation(
    (trainerId: number) => adminService.deleteTrainer(trainerId),
    {
      onSuccess: (data) => {
        toast.success(data.message);
        queryClient.invalidateQueries(['admin-trainers']);
        setSelectedTrainer(null);
        setShowStats(false);
      },
      onError: (error: any) => {
        // Пробуем разные варианты получения сообщения об ошибке
        const errorMessage = 
          error.detail || 
          error.response?.data?.detail || 
          error.response?.data?.message || 
          error.message || 
          'Ошибка при удалении тренера';
        
        toast.error(errorMessage);
      }
    }
  );

  // Мутация для изменения пароля тренера
  const changePasswordMutation = useMutation(
    ({ trainerId, newPassword }: { trainerId: number; newPassword: string }) => 
      adminService.changeTrainerPassword(trainerId, newPassword),
    {
      onSuccess: (data) => {
        toast.success(data.message);
        setShowPasswordModal(false);
        setPasswordTrainer(null);
        setNewPassword('');
      },
      onError: (error: any) => {
        const errorMessage = 
          error.detail || 
          error.response?.data?.detail || 
          error.response?.data?.message || 
          error.message || 
          'Ошибка при изменении пароля';
        
        toast.error(errorMessage);
      }
    }
  );

  // Получение статистики тренера
  const getTrainerStats = async (trainerId: number) => {
    try {
      const stats = await adminService.getTrainerStats(trainerId);
      setStatsData(stats);
      setShowStats(true);
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Ошибка при получении статистики');
    }
  };

  const handleToggleAdmin = (trainer: Trainer) => {
    if (window.confirm(
      `Вы уверены, что хотите ${trainer.is_admin ? 'снять' : 'назначить'} права администратора для ${trainer.first_name} ${trainer.last_name}?`
    )) {
      toggleAdminMutation.mutate(trainer.id);
    }
  };

  const handleToggleActive = (trainer: Trainer) => {
    if (window.confirm(
      `Вы уверены, что хотите ${trainer.is_active ? 'заблокировать' : 'разблокировать'} тренера ${trainer.first_name} ${trainer.last_name}?`
    )) {
      toggleActiveMutation.mutate(trainer.id);
    }
  };

  const handleViewStats = (trainer: Trainer) => {
    setSelectedTrainer(trainer);
    getTrainerStats(trainer.id);
  };

  const handleDeleteTrainer = (trainer: Trainer) => {
    if (window.confirm(
      `Вы уверены, что хотите удалить тренера ${trainer.first_name} ${trainer.last_name}? Это действие необратимо.`
    )) {
      deleteTrainerMutation.mutate(trainer.id);
    }
  };

  const handleChangePassword = (trainer: Trainer) => {
    setPasswordTrainer(trainer);
    setShowPasswordModal(true);
  };

  const handlePasswordSubmit = () => {
    if (!passwordTrainer || !newPassword.trim()) {
      toast.error('Введите новый пароль');
      return;
    }
    
    if (newPassword.length < 6) {
      toast.error('Пароль должен содержать минимум 6 символов');
      return;
    }

    changePasswordMutation.mutate({
      trainerId: passwordTrainer.id,
      newPassword: newPassword.trim()
    });
  };

  const clearFilters = () => {
    setSearchTerm('');
    setActiveFilter(null);
    setAdminFilter(null);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('ru-RU', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navigation currentPage="admin" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="bg-red-50 border border-red-200 rounded-md p-4">
            <div className="flex">
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">
                  Ошибка загрузки данных
                </h3>
                <div className="mt-2 text-sm text-red-700">
                  {(error as any)?.response?.data?.detail || 'Произошла ошибка при загрузке списка тренеров'}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navigation currentPage="admin" />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Заголовок */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Администрирование тренеров</h1>
          <p className="mt-2 text-gray-600">
            Управление тренерами, их правами и статусом активности
          </p>
        </div>

        {/* Фильтры и поиск */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* Поиск */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="text"
                placeholder="Поиск по имени или email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            {/* Фильтр по активности */}
            <select
              value={activeFilter === null ? '' : activeFilter.toString()}
              onChange={(e) => setActiveFilter(e.target.value === '' ? null : e.target.value === 'true')}
              className="px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">Все тренеры</option>
              <option value="true">Активные</option>
              <option value="false">Заблокированные</option>
            </select>

            {/* Фильтр по правам администратора */}
            <select
              value={adminFilter === null ? '' : adminFilter.toString()}
              onChange={(e) => setAdminFilter(e.target.value === '' ? null : e.target.value === 'true')}
              className="px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">Все права</option>
              <option value="true">Администраторы</option>
              <option value="false">Обычные тренеры</option>
            </select>

            {/* Кнопка очистки фильтров */}
            <button
              onClick={clearFilters}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors duration-200 flex items-center justify-center"
            >
              <Filter size={16} className="mr-2" />
              Очистить
            </button>
          </div>
        </div>

        {/* Статистика */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-600">{trainers.length}</div>
              <div className="text-sm text-gray-600">Всего тренеров</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600">
                {trainers.filter(t => t.is_active).length}
              </div>
              <div className="text-sm text-gray-600">Активных</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-red-600">
                {trainers.filter(t => !t.is_active).length}
              </div>
              <div className="text-sm text-gray-600">Заблокированных</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-purple-600">
                {trainers.filter(t => t.is_admin).length}
              </div>
              <div className="text-sm text-gray-600">Администраторов</div>
            </div>
          </div>
        </div>

        {/* Таблица тренеров */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
            <h2 className="text-lg font-medium text-gray-900">Список тренеров</h2>
            <button
              onClick={() => refetch()}
              disabled={isLoading}
              className="px-3 py-1 bg-blue-100 text-blue-700 rounded-md hover:bg-blue-200 transition-colors duration-200 flex items-center"
            >
              <RefreshCw size={16} className={`mr-2 ${isLoading ? 'animate-spin' : ''}`} />
              Обновить
            </button>
          </div>

          {isLoading ? (
            <div className="p-8 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
              <p className="mt-2 text-gray-600">Загрузка тренеров...</p>
            </div>
          ) : trainers.length === 0 ? (
            <div className="p-8 text-center">
              <Users size={48} className="mx-auto text-gray-400 mb-4" />
              <p className="text-gray-600">Тренеры не найдены</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Тренер
                    </th>
                    <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Email
                    </th>
                    <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Статус
                    </th>
                    <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Права
                    </th>
                    <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Действия
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {trainers.map((trainer) => (
                    <tr key={trainer.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10">
                            <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                              <span className="text-sm font-medium text-blue-600">
                                {trainer.first_name[0]}{trainer.last_name[0]}
                              </span>
                            </div>
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">
                              {trainer.first_name} {trainer.last_name}
                            </div>
                            <div className="text-sm text-gray-500">ID: {trainer.id}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-2 py-4 whitespace-nowrap text-sm text-gray-900">
                        {trainer.email}
                      </td>
                      <td className="px-2 py-4 whitespace-nowrap">
                        <div>
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            trainer.is_active
                              ? 'bg-green-100 text-green-800'
                              : 'bg-red-100 text-red-800'
                          }`}>
                            {trainer.is_active ? 'Активен' : 'Заблокирован'}
                          </span>
                          {trainer.last_login && (
                            <div className="text-xs text-gray-500 mt-1">
                              Последний вход: {formatDate(trainer.last_login)}
                            </div>
                          )}
                          {trainer.last_activity && (
                            <div className="text-xs text-gray-500 mt-1">
                              Последние действия: {formatDate(trainer.last_activity)}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-2 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          trainer.is_admin
                            ? 'bg-purple-100 text-purple-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}>
                          {trainer.is_admin ? 'Администратор' : 'Тренер'}
                        </span>
                      </td>
                      <td className="px-2 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex space-x-2">
                          <button
                            onClick={() => handleViewStats(trainer)}
                            className="text-blue-600 hover:text-blue-900 p-1"
                            title="Просмотр статистики"
                          >
                            <BarChart3 size={16} />
                          </button>
                          <button
                            onClick={() => handleToggleAdmin(trainer)}
                            disabled={toggleAdminMutation.isLoading}
                            className={`p-1 ${
                              trainer.is_admin
                                ? 'text-red-600 hover:text-red-900'
                                : 'text-green-600 hover:text-green-900'
                            }`}
                            title={trainer.is_admin ? 'Снять права администратора' : 'Назначить администратором'}
                          >
                            {trainer.is_admin ? <ShieldOff size={16} /> : <Shield size={16} />}
                          </button>
                          <button
                            onClick={() => handleToggleActive(trainer)}
                            disabled={toggleActiveMutation.isLoading}
                            className={`p-1 ${
                              trainer.is_active
                                ? 'text-red-600 hover:text-red-900'
                                : 'text-green-600 hover:text-green-900'
                            }`}
                            title={trainer.is_active ? 'Заблокировать' : 'Разблокировать'}
                          >
                            {trainer.is_active ? <UserX size={16} /> : <UserCheck size={16} />}
                          </button>
                          <button
                            onClick={() => handleChangePassword(trainer)}
                            disabled={changePasswordMutation.isLoading}
                            className="text-blue-600 hover:text-blue-900 p-1"
                            title="Изменить пароль"
                          >
                            <Key size={16} />
                          </button>
                          {trainer.id !== currentTrainer?.id ? (
                            trainer.active_clients_count && trainer.active_clients_count > 0 ? (
                              <button
                                disabled={true}
                                className="text-gray-400 p-1 cursor-not-allowed"
                                title={`Нельзя удалить тренера с активными клиентами (${trainer.active_clients_count} клиентов)`}
                              >
                                <Trash2 size={16} />
                              </button>
                            ) : (
                              <button
                                onClick={() => handleDeleteTrainer(trainer)}
                                disabled={deleteTrainerMutation.isLoading}
                                className="text-red-600 hover:text-red-900 p-1"
                                title="Удалить тренера"
                              >
                                <Trash2 size={16} />
                              </button>
                            )
                          ) : null}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Модальное окно статистики */}
        {showStats && statsData && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b border-gray-200">
                <div className="flex justify-between items-center">
                  <h3 className="text-lg font-medium text-gray-900">
                    Статистика тренера: {statsData.trainer_name}
                  </h3>
                  <button
                    onClick={() => setShowStats(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    ✕
                  </button>
                </div>
              </div>
              
              <div className="p-6">
                {/* Основная информация */}
                <div className="grid grid-cols-2 gap-4 mb-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Email</label>
                    <p className="mt-1 text-sm text-gray-900">{statsData.email}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Дата регистрации</label>
                    <p className="mt-1 text-sm text-gray-900">{formatDate(statsData.created_at)}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Статус</label>
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full mt-1 ${
                      statsData.is_active
                        ? 'bg-green-100 text-green-800'
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {statsData.is_active ? 'Активен' : 'Заблокирован'}
                    </span>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Права</label>
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full mt-1 ${
                      statsData.is_admin
                        ? 'bg-purple-100 text-purple-800'
                        : 'bg-gray-100 text-gray-800'
                    }`}>
                      {statsData.is_admin ? 'Администратор' : 'Тренер'}
                    </span>
                  </div>
                </div>

                {/* Статистика */}
                <div className="bg-gray-50 rounded-lg p-4 mb-6">
                  <h4 className="text-sm font-medium text-gray-900 mb-3">Статистика активности</h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="text-center">
                      <div className="text-2xl font-bold text-blue-600">{statsData.stats.active_clients_count}</div>
                                              <div className="text-xs text-gray-600">Активных клиентов</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-green-600">{statsData.stats.exercises_count}</div>
                      <div className="text-xs text-gray-600">Упражнений</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-purple-600">{statsData.stats.workouts_count}</div>
                      <div className="text-xs text-gray-600">Тренировок</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-orange-600">{statsData.stats.action_logs_count}</div>
                      <div className="text-xs text-gray-600">Действий</div>
                    </div>
                  </div>
                </div>

                {/* Последние действия */}
                <div>
                  <h4 className="text-sm font-medium text-gray-900 mb-3">Последние действия</h4>
                  {statsData.recent_actions.length > 0 ? (
                    <div className="space-y-2">
                      {statsData.recent_actions.map((action, index) => (
                        <div key={index} className="flex justify-between items-start p-3 bg-gray-50 rounded-md">
                          <div className="flex-1">
                            <p className="text-sm font-medium text-gray-900">{action.description}</p>
                            <p className="text-xs text-gray-500">
                              {action.action_type} • {action.resource_type}
                            </p>
                          </div>
                          <span className="text-xs text-gray-400 ml-2">
                            {formatDate(action.created_at)}
                          </span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500">Нет данных о действиях</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Модальное окно изменения пароля */}
        {showPasswordModal && passwordTrainer && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg max-w-md w-full">
              <div className="p-6 border-b border-gray-200">
                <div className="flex justify-between items-center">
                  <h3 className="text-lg font-medium text-gray-900">
                    Изменить пароль тренера
                  </h3>
                  <button
                    onClick={() => {
                      setShowPasswordModal(false);
                      setPasswordTrainer(null);
                      setNewPassword('');
                    }}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    ✕
                  </button>
                </div>
                <p className="mt-2 text-sm text-gray-600">
                  {passwordTrainer.first_name} {passwordTrainer.last_name} ({passwordTrainer.email})
                </p>
              </div>
              
              <div className="p-6">
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Новый пароль
                  </label>
                  <input
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Введите новый пароль"
                    minLength={6}
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Минимум 6 символов
                  </p>
                </div>
                
                <div className="flex justify-end space-x-3">
                  <button
                    onClick={() => {
                      setShowPasswordModal(false);
                      setPasswordTrainer(null);
                      setNewPassword('');
                    }}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                  >
                    Отмена
                  </button>
                  <button
                    onClick={handlePasswordSubmit}
                    disabled={changePasswordMutation.isLoading || !newPassword.trim()}
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {changePasswordMutation.isLoading ? 'Изменение...' : 'Изменить пароль'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminPage; 